import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import EmailAddress from "_shared/email-addresses/email-address";
import Name from "_shared/names/name";
import RegistrationInfoSchema, {
  RegistrationInfoJSON,
  RegistrationInfoParams,
} from "legal-entities/registration-info/_schema/registration-info-schema";
import Password from "users/auth/credentials/passwords/password";
import ValueObject from "_framework/entities/values/value-object";

// TODO: A server-side RegistrationInfo class does not exist.
export default class RegistrationInfo extends ValueObject<
  RegistrationInfo,
  RegistrationInfoJSON,
  RegistrationInfoParams
> {
  public static readonly EXAMPLE: RegistrationInfo = RegistrationInfo.create({
    params: {
      name: Name.create({params: { firstName: "Test", lastName: "User" }}),
      email: EmailAddress.create("test@example.com"),
      password: Password.create({ password: "TestPassword123!" }),
    }
  });

  public static create(args: { params: RegistrationInfoParams, options?: { silenceSchemaErrors: boolean } }): RegistrationInfo {
    const parsed = RegistrationInfoSchema.validate(args.params, !!args.options?.silenceSchemaErrors);
    return new RegistrationInfo(parsed);
  }

  public static fromJSON(json: unknown): RegistrationInfo | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = RegistrationInfoSchema.deserialize(json);
    return new RegistrationInfo(params);
  }

  public readonly name: Name;
  public readonly email: EmailAddress;
  public readonly password?: Password;

  public constructor(values: RegistrationInfoParams) {
    super(values);
    this.name = Name.create({ params: values.name });
    this.email = EmailAddress.create(values.email.value);
    this.password = values.password ? Password.create(values.password) : undefined;
  }

  public toJSON(): RegistrationInfoJSON {
    return {
      name: this.name.toJSON(),
      email: this.email.toJSON(),
      password: this.password?.toJSON(),
    };
  }

  public isEqualTo(other: RegistrationInfo): boolean {
    const sameName = this.name.isEqualTo(other?.name);
    const sameEmail = this.email.isEqualTo(other?.email);
    const samePassword =
      (this.password === undefined && other.password === undefined) ||
      (this.password !== undefined && other.password !== undefined && this.password.isEqualTo(other.password));

    return sameName && sameEmail && samePassword;
  }

  public toString(): string {
    return `RegistrationInfo(
      Name: ${this.name.toString()}, 
      Email: ${this.email.toString()}, 
      Password: ${this.password ? "****" : "undefined"}
    )`;
  }
}

export class InvalidRegistrationInfoError extends Error {
  issues: unknown;

  constructor(issues: unknown) {
    super(`Invalid registration info.`);
    this.issues = issues;
  }
}
